import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    makeStyles,
    withStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import { Button, Modal } from 'antd';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import DesignSummaryFilterPanel from '../../components/DesignSummaryFilterPanel';
import { buildTwoBucketStageSummaryCsv, sanitizeCsvCell } from '../../utils/designSummaryTableCsv';

const NAVY = '#1C375D';
const ORANGE = '#F47E46';
const GRAND_TOTAL_GREEN = '#355025';
const STRIPE_ODD = '#F3F6FA';
const STRIPE_EVEN = '#FFFFFF';
const TABLE_BORDER = '#e8e0dc';
const TABLE_HEAD_BG = NAVY;
const TABLE_HEAD_TEXT = '#FFF2EB';
const OTHER_DESIGN_STAGES_KEY = 'otherDesignStages';
const ALERT_RED = '#C62828';
const ALERT_RED_BG = '#FFEBEE';

/** Same table styling as Design Summary */
const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: NAVY,
        color: '#FFF2EB',
        fontWeight: 500,
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const StyledTableRow = withStyles({
    root: {},
})(TableRow);

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(1, 2),
    },
    pageHeader: {
        marginBottom: theme.spacing(1),
    },
    title: {
        color: NAVY,
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: 14,
        marginBottom: theme.spacing(1),
    },
    bordercolors: {
        border: `1px solid ${TABLE_BORDER}`,
    },
    summaryTablePaper: {
        borderRadius: 8,
        border: `1px solid ${TABLE_BORDER}`,
        boxShadow: '0 1px 4px rgba(28, 55, 93, 0.08)',
    },
    tableScrollContainer: {
        maxHeight: 'min(600px, calc(100vh - 260px))',
        overflow: 'auto',
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
        '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
        },
    },
    stickyTableHead: {
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: TABLE_HEAD_BG,
        '& .MuiTableCell-head': {
            backgroundColor: `${TABLE_HEAD_BG} !important`,
            color: `${TABLE_HEAD_TEXT} !important`,
            boxShadow: `inset 0 -1px 0 ${TABLE_BORDER}`,
        },
    },
    summaryTable: {
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    filterAccordion: {
        width: '100%',
        marginBottom: theme.spacing(2),
        border: '1px solid #eee5e5',
        borderRadius: '8px !important',
        overflow: 'visible',
        boxShadow: 'none',
        position: 'relative',
        zIndex: 20,
        '&:before': { display: 'none' },
        '& .MuiAccordionDetails-root': {
            overflow: 'visible',
        },
    },
    filterAccordionSummary: {
        backgroundColor: NAVY,
        color: '#FFF2EB',
        minHeight: 48,
        '&.Mui-expanded': { minHeight: 48 },
        '& .MuiAccordionSummary-content': {
            margin: '10px 0',
        },
        '& .MuiAccordionSummary-expandIcon': {
            color: '#FFF2EB',
        },
    },
    filterAccordionDetails: {
        display: 'block !important',
        width: '100%',
        padding: theme.spacing(1.5, 2, 2),
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        overflow: 'visible !important',
    },
    filterAccordionInner: {
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'visible',
    },
    tableScroll: {
        position: 'relative',
        zIndex: 1,
    },
    tableLoadingOverlay: {
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
    },
    bodyCell: {
        textAlign: 'center',
        border: `1px solid ${TABLE_BORDER}`,
        fontSize: 14,
    },
    bodyCellOdd: {
        backgroundColor: STRIPE_ODD,
    },
    bodyCellEven: {
        backgroundColor: STRIPE_EVEN,
    },
    summaryHeadCell: {
        '&&': {
            backgroundColor: `${TABLE_HEAD_BG} !important`,
            color: `${TABLE_HEAD_TEXT} !important`,
        },
        textAlign: 'center',
        fontWeight: 500,
        border: `1px solid ${TABLE_BORDER}`,
        verticalAlign: 'middle',
        padding: '10px 8px',
        lineHeight: 1.35,
        whiteSpace: 'nowrap',
    },
    expCentreHeadCell: {
        position: 'sticky',
        left: 0,
        zIndex: 16,
        minWidth: 168,
        maxWidth: 168,
        width: 168,
        fontWeight: 600,
        verticalAlign: 'middle',
        boxShadow: '2px 0 4px rgba(0,0,0,0.08)',
    },
    expCentreBodyCell: {
        position: 'sticky',
        left: 0,
        zIndex: 4,
        minWidth: 168,
        maxWidth: 168,
        verticalAlign: 'middle',
        textAlign: 'center',
        border: `1px solid ${TABLE_BORDER}`,
        fontWeight: 600,
        backgroundColor: `${TABLE_HEAD_BG} !important`,
        color: `${TABLE_HEAD_TEXT} !important`,
        boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
    },
    expCentreTotalsCell: {
        position: 'sticky',
        left: 0,
        zIndex: 5,
        minWidth: 168,
        maxWidth: 168,
        textAlign: 'center',
        border: `1px solid ${TABLE_BORDER}`,
        borderTop: `2px solid ${NAVY}`,
        fontWeight: 700,
        fontSize: 15,
        backgroundColor: `${TABLE_HEAD_BG} !important`,
        color: `${TABLE_HEAD_TEXT} !important`,
        boxShadow: '2px 0 4px rgba(0,0,0,0.06)',
    },
    detailBodyCell: {
        border: `1px solid ${TABLE_BORDER}`,
        fontSize: 13,
    },
    detailBodyCellOdd: {
        backgroundColor: STRIPE_ODD,
    },
    detailBodyCellEven: {
        backgroundColor: STRIPE_EVEN,
    },
    clickableCount: {
        color: NAVY,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'normal',
    },
    clickableGrandTotal: {
        color: GRAND_TOTAL_GREEN,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 'bolder',
    },
    disabledCount: {
        color: NAVY,
        fontWeight: 'normal',
    },
    otherDesignStagesAlertCell: {
        backgroundColor: `${ALERT_RED_BG} !important`,
    },
    clickableAlertCount: {
        color: ALERT_RED,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 700,
    },
    totalsRowAlertCount: {
        color: ALERT_RED,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 700,
    },
    totalsTableRow: {
        '& td': {
            borderTop: `2px solid ${NAVY}`,
        },
    },
    totalsRowCell: {
        textAlign: 'center',
        backgroundColor: '#FFFFFF',
        border: `1px solid ${TABLE_BORDER}`,
        fontWeight: 700,
        fontSize: 15,
        color: NAVY,
    },
    totalsRowCount: {
        color: NAVY,
        textDecoration: 'underline',
        cursor: 'pointer',
        fontWeight: 700,
    },
    totalsRowCountDisabled: {
        color: NAVY,
        fontWeight: 700,
    },
    emptyState: {
        textAlign: 'center',
        padding: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    csv: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 12,
        flexShrink: 0,
    },
    tableDownloadRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    summaryDownloadBtn: {
        backgroundColor: GRAND_TOTAL_GREEN,
        color: '#FFF2EB',
    },
    detailModalContent: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(80vh - 110px)',
    },
    detailTable: {
        flex: 1,
        minHeight: 240,
        maxHeight: 'calc(80vh - 160px)',
        overflow: 'auto',
        width: '100%',
    },
    detailTableInner: {
        minWidth: 1680,
    },
}));

const DEFAULT_STAGE_CONFIG = [
    { key: 'designerAssigned', label: 'Designer Assigned', threshold: 2 },
    { key: 'initialMeasurement', label: 'Initial Measurement', threshold: 3 },
    { key: 'designDiscussion', label: 'Design Discussion', threshold: 15 },
    { key: 'renders', label: 'Renders', threshold: 5 },
    { key: 'finalizationOfDesign', label: 'Finalization in Design', threshold: 5 },
    { key: 'finalizationOfMaterials', label: 'Finalization of Materials', threshold: 5 },
    { key: 'siteQc', label: 'Site QC/Project Kick-Off', threshold: 3 },
    { key: 'drawingQc', label: 'Drawing QC', threshold: 3 },
    { key: 'finalQuoteAndDesign', label: 'Final Quote and Design', threshold: 5 },
    { key: 'otherDesignStages', label: 'Other Design Stages', threshold: 0 },
];

const formatDate = (value) => (value ? moment(value).format('DD-MM-YYYY') : '-');

const pickFirstValidDate = (...candidates) => {
    for (const candidate of candidates) {
        if (candidate == null || candidate === '') continue;
        const parsed = moment(candidate);
        if (parsed.isValid()) return candidate;
    }
    return null;
};

const getFirstDesignDiscussionDate = (lead = {}) => {
    const details = lead.designDiscussionDetails || [];
    if (!Array.isArray(details) || !details.length) return null;
    const dated = details
        .map((item) => pickFirstValidDate(item?.date, item?.discussionDate, item?.createdAt))
        .filter(Boolean)
        .sort((a, b) => moment(a).valueOf() - moment(b).valueOf());
    return dated[0] || null;
};

const STAGE_DATE_RESOLVERS = {
    designerAssigned: (row) =>
        pickFirstValidDate(row?.stageDate, row?.designerAssignedDate, row?.leadId?.designerAssignedDate),
    initialMeasurement: (row) =>
        pickFirstValidDate(row?.stageDate, row?.intial_measurement_date, row?.leadId?.intial_measurement_date),
    designDiscussion: (row) =>
        pickFirstValidDate(row?.stageDate, row?.designDiscussionDate, getFirstDesignDiscussionDate(row?.leadId)),
    renders: (row) =>
        pickFirstValidDate(row?.stageDate, row?.rendersDate, row?.leadId?.rendersDate),
    finalizationOfDesign: (row) =>
        pickFirstValidDate(row?.stageDate, row?.finalizationOfDesignDate, row?.leadId?.finalizationOfDesignDate),
    finalizationOfMaterials: (row) =>
        pickFirstValidDate(row?.stageDate, row?.materialSelectionDate, row?.leadId?.materialSelectionDate),
    siteQc: (row) =>
        pickFirstValidDate(row?.stageDate, row?.siteQcDate, row?.leadId?.siteQcDate),
    drawingQc: (row) =>
        pickFirstValidDate(row?.stageDate, row?.drawingQcDate, row?.leadId?.drawingQcDate),
    finalQuoteAndDesign: (row) =>
        pickFirstValidDate(row?.stageDate, row?.finalQuoteAndDesignDate, row?.leadId?.finalQuoteAndDesignDate),
    otherDesignStages: (row) => pickFirstValidDate(row?.stageDate),
};

const resolveStageKeyFromCurrentStage = (currentStage = '', stageConfig = []) => {
    if (!currentStage) return '';
    const exact = stageConfig.find((stage) => stage.label === currentStage);
    if (exact) return exact.key;
    const fuzzy = stageConfig.find(
        (stage) => currentStage.includes(stage.label) || stage.label.includes(currentStage)
    );
    return fuzzy?.key || '';
};

const resolveDetailStageDate = (row, stageKey, stageConfig) => {
    const effectiveKey = stageKey || resolveStageKeyFromCurrentStage(row?.leadId?.currentStage, stageConfig);
    if (!effectiveKey) return null;
    const resolver = STAGE_DATE_RESOLVERS[effectiveKey];
    return resolver ? resolver(row) : pickFirstValidDate(row?.stageDate);
};

const parseUrlFilterParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        startDate: params.get('startDate') || '',
        endDate: params.get('endDate') || '',
        selectedExperienceCenter: params.get('selectedExperienceCenter') || '',
        selectedLocation: params.get('selectedLocation') || '',
        token: params.get('token') || '',
        users: params.get('users') || '',
        selectedRole: params.get('selectedRole') || '',
        userExpCenter: params.get('userExpCenter') || '',
        userLocation: params.get('userLocation') || '',
        searchType: params.get('searchType') || '',
        search: params.get('search') || '',
    };
};

const DesignStageDaysSummary = (props) => {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [stageConfig, setStageConfig] = useState(DEFAULT_STAGE_CONFIG);
    const [grandTotal, setGrandTotal] = useState(0);
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailTitle, setDetailTitle] = useState('Overall Summary');
    const [detailRows, setDetailRows] = useState([]);
    const [detailStageKey, setDetailStageKey] = useState('');
    const [csvData, setCsvData] = useState([]);
    const [apiParams, setApiParams] = useState(parseUrlFilterParams);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const detailTableRef = useRef(null);

    const initialParams = useMemo(() => parseUrlFilterParams(), []);

    const detailStageDateColumnLabel = useMemo(() => {
        if (!detailStageKey) return 'Stage Date';
        const stage = stageConfig.find((item) => item.key === detailStageKey);
        return stage ? `${stage.label} Date` : 'Stage Date';
    }, [detailStageKey, stageConfig]);

    useEffect(() => {
        if (!detailOpen || !detailTableRef.current) return;
        detailTableRef.current.scrollTop = 0;
        detailTableRef.current.scrollLeft = 0;
    }, [detailOpen, detailRows, detailTitle]);

    const loadSummary = useCallback(async (params) => {
        setSummaryLoading(true);
        try {
            const response = await HttpService.designStageDaysSummary(params);
            setApiParams(params);
            setRows(response.data?.data || []);
            setTotals(response.data?.totals || null);
            setGrandTotal(response.data?.grandTotal || response.data?.totalCountedInStages || 0);
            if (Array.isArray(response.data?.stageConfig) && response.data.stageConfig.length) {
                setStageConfig(response.data.stageConfig);
            }
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.error || error?.response?.data || 'Failed to load design stage days summary',
                'error'
            );
        } finally {
            setSummaryLoading(false);
        }
    }, [props.setSnackInfo]);

    useEffect(() => {
        loadSummary(initialParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getRowGrandTotal = (stages = {}) =>
        stageConfig.reduce((sum, stage) => {
            const bucket = stages?.[stage.key] || {};
            return sum + (Number(bucket.lte) || 0) + (Number(bucket.gt) || 0);
        }, 0);

    const summaryTableCsv = useMemo(
        () =>
            buildTwoBucketStageSummaryCsv({
                stageConfig,
                rows,
                totals,
            }),
        [stageConfig, rows, totals]
    );

    const summaryCsvFilename = useMemo(
        () => `design-stage-days-summary-${moment().format('YYYY-MM-DD')}.csv`,
        [rows, totals]
    );

    const buildCsv = (leads = [], stageKey = '') =>
        leads.map((data, index) => ({
            serialNo: index + 1,
            projectCode: data?.leadId?.erpProjectNo || 'N/A',
            leadNumber: data?.leadId?.lead_no || 'N/A',
            clientName: data?.leadId?.customerId?.name || 'N/A',
            currentStage: data?.leadId?.currentStage || 'N/A',
            stageDate: formatDate(resolveDetailStageDate(data, stageKey, stageConfig)),
            valueInr: data?.leadId?.staticSalesWonBudget ?? 'N/A',
            salesWonValue: data?.leadId?.staticSalesWonBudget ?? 'N/A',
            designWonValue: data?.leadId?.staticDesignWonBudget ?? 'N/A',
            city: data?.salesExpCenter?.locationId?.name || data?.leadId?.experienceCenterId?.locationId?.name || 'N/A',
            expCtrName: data?.salesExpCenter?.name || 'N/A',
            designExpCtrName: data?.DesignExpCenter?.name || data?.leadId?.designWonExpCtrId?.name || 'N/A',
            salesWonDate: formatDate(data?.SalesWonDate || data?.leadId?.leadWonDate),
            designSignOffDate: formatDate(data?.leadId?.customerDesignSignOffDate),
            materialDeliveryDate: formatDate(data?.leadId?.materialDispatchedDate),
            actualCompletionDate: formatDate(data?.leadId?.executionCompletionDate),
            latestNotes: sanitizeCsvCell(data?.latestNotesMsg),
            latestDealActivity: sanitizeCsvCell(data?.latestDealMsg),
        }));

    const openDetails = async ({ experienceCentre, stageKey, bucket, label, count }) => {
        if (!count) return;

        props.setLoading(true);
        try {
            const response = await HttpService.designStageDaysDetails({
                ...apiParams,
                tokenPercentage: apiParams.token,
                experienceCentre: experienceCentre || 'Total',
                stageKey: stageKey || '',
                bucket: bucket || 'all',
            });
            const leads = Array.isArray(response.data) ? response.data : [];
            setDetailStageKey(stageKey || '');
            setDetailRows(leads);
            setCsvData(buildCsv(leads, stageKey || ''));
            setDetailTitle(`${label} (${leads.length})`);
            setDetailOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.error || error?.response?.data || 'Failed to load details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const isOtherDesignStagesAlert = (stageKey, count) =>
        stageKey === OTHER_DESIGN_STAGES_KEY && Number(count) > 0;

    const renderCount = (
        count,
        onClick,
        { isGrandTotal = false, isTotalsRow = false, isOtherDesignAlert = false } = {}
    ) => {
        if (!count) {
            return (
                <span className={isTotalsRow ? classes.totalsRowCountDisabled : classes.disabledCount}>
                    0
                </span>
            );
        }
        let countClass = classes.clickableCount;
        if (isOtherDesignAlert) {
            countClass = isTotalsRow ? classes.totalsRowAlertCount : classes.clickableAlertCount;
        } else if (isGrandTotal) countClass = classes.clickableGrandTotal;
        else if (isTotalsRow) countClass = classes.totalsRowCount;

        return (
            <span
                className={countClass}
                onClick={onClick}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') onClick();
                }}
            >
                {count}
            </span>
        );
    };

    return (
        <div className={classes.root}>
            <Box className={classes.pageHeader}>
                <Typography className={classes.title} variant="h5">
                    Design Stage Days Summary
                </Typography>
                <Typography className={classes.subtitle}>
                    Design column break-up by experience centre and days in current stage.
                    Projects without a stage date are counted under the &gt; Days column.
                    {grandTotal > 0 ? ` Total projects: ${grandTotal}.` : ''}
                </Typography>
            </Box>

            {userDetails?._id && (
                <Accordion className={classes.filterAccordion} defaultExpanded={false} TransitionProps={{ unmountOnExit: false }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className={classes.filterAccordionSummary}
                    >
                        <Typography style={{ fontWeight: 600, fontSize: 14 }}>
                            Filters &amp; Search
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.filterAccordionDetails}>
                        <Box className={classes.filterAccordionInner}>
                            <DesignSummaryFilterPanel
                                embedded
                                userDetails={userDetails}
                                initialParams={initialParams}
                                onApply={loadSummary}
                                setSnackInfo={props.setSnackInfo}
                            />
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}

            <Box className={classes.tableDownloadRow}>
                {summaryTableCsv.data.length > 0 && !summaryLoading ? (
                    <CSVLink
                        data={summaryTableCsv.data}
                        headers={summaryTableCsv.headers}
                        filename={summaryCsvFilename}
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            variant="contained"
                            className={classes.summaryDownloadBtn}
                        >
                            Download table CSV
                        </Button>
                    </CSVLink>
                ) : (
                    <Button
                        variant="contained"
                        className={classes.summaryDownloadBtn}
                        disabled={summaryLoading}
                        onClick={() =>
                            props.setSnackInfo('No summary data to download for the current filters.', 'warning')
                        }
                    >
                        Download table CSV
                    </Button>
                )}
            </Box>

            <div className={classes.tableScroll}>
                {summaryLoading && (
                    <Box className={classes.tableLoadingOverlay} aria-busy="true" aria-label="Loading summary">
                        <CircularProgress style={{ color: ORANGE }} />
                    </Box>
                )}
                <TableContainer
                    component={Paper}
                    className={`${classes.summaryTablePaper} ${classes.tableScrollContainer}`}
                    style={{ marginTop: 0 }}
                >
                    <Table className={classes.summaryTable} style={{ minWidth: 1100 }}>
                        <TableHead className={classes.stickyTableHead}>
                            <StyledTableRow>
                                <StyledTableCell
                                    className={`${classes.summaryHeadCell} ${classes.expCentreHeadCell}`}
                                    rowSpan={2}
                                >
                                    Experience Centre
                                </StyledTableCell>
                                {stageConfig.map((stage) => (
                                    <StyledTableCell
                                        key={stage.key}
                                        className={classes.summaryHeadCell}
                                        colSpan={2}
                                    >
                                        {stage.label}
                                    </StyledTableCell>
                                ))}
                                <StyledTableCell
                                    className={classes.summaryHeadCell}
                                    rowSpan={2}
                                    style={{ fontSize: 15 }}
                                >
                                    Grand Total
                                </StyledTableCell>
                            </StyledTableRow>
                            <StyledTableRow>
                                {stageConfig.map((stage) => (
                                    <React.Fragment key={`${stage.key}-days`}>
                                        <StyledTableCell className={classes.summaryHeadCell}>
                                            {`<= ${stage.threshold} Days`}
                                        </StyledTableCell>
                                        <StyledTableCell className={classes.summaryHeadCell}>
                                            {`> ${stage.threshold} Days`}
                                        </StyledTableCell>
                                    </React.Fragment>
                                ))}
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                        {rows.map((row, rowIndex) => {
                            const rowTotal = getRowGrandTotal(row.stages);
                            const isOddRow = rowIndex % 2 === 0;
                            const stripeBody = isOddRow ? classes.bodyCellOdd : classes.bodyCellEven;
                            return (
                                <StyledTableRow key={row.experienceCentre}>
                                    <StyledTableCell className={classes.expCentreBodyCell}>
                                        {row.experienceCentre}
                                    </StyledTableCell>
                                    {stageConfig.map((stage) => {
                                        const lte = row.stages?.[stage.key]?.lte ?? 0;
                                        const gt = row.stages?.[stage.key]?.gt ?? 0;
                                        const lteAlert = isOtherDesignStagesAlert(stage.key, lte);
                                        const gtAlert = isOtherDesignStagesAlert(stage.key, gt);
                                        return (
                                            <React.Fragment key={`${row.experienceCentre}-${stage.key}`}>
                                                <StyledTableCell
                                                    className={`${classes.bordercolors} ${classes.bodyCell} ${stripeBody} ${
                                                        lteAlert ? classes.otherDesignStagesAlertCell : ''
                                                    }`}
                                                >
                                                    {renderCount(
                                                        lte,
                                                        () =>
                                                            openDetails({
                                                                experienceCentre: row.experienceCentre,
                                                                stageKey: stage.key,
                                                                bucket: 'lte',
                                                                label: `${row.experienceCentre} · ${stage.label} · <= ${stage.threshold} Days`,
                                                                count: lte,
                                                            }),
                                                        { isOtherDesignAlert: lteAlert }
                                                    )}
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    className={`${classes.bordercolors} ${classes.bodyCell} ${stripeBody} ${
                                                        gtAlert ? classes.otherDesignStagesAlertCell : ''
                                                    }`}
                                                >
                                                    {renderCount(
                                                        gt,
                                                        () =>
                                                            openDetails({
                                                                experienceCentre: row.experienceCentre,
                                                                stageKey: stage.key,
                                                                bucket: 'gt',
                                                                label: `${row.experienceCentre} · ${stage.label} · > ${stage.threshold} Days`,
                                                                count: gt,
                                                            }),
                                                        { isOtherDesignAlert: gtAlert }
                                                    )}
                                                </StyledTableCell>
                                            </React.Fragment>
                                        );
                                    })}
                                    <StyledTableCell className={`${classes.bordercolors} ${classes.bodyCell} ${stripeBody}`}>
                                        {renderCount(rowTotal, () =>
                                            openDetails({
                                                experienceCentre: row.experienceCentre,
                                                stageKey: '',
                                                bucket: 'all',
                                                label: `${row.experienceCentre} · Grand Total`,
                                                count: rowTotal,
                                            }), { isGrandTotal: true })}
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })}
                        {totals && (
                            <StyledTableRow className={classes.totalsTableRow}>
                                <StyledTableCell className={classes.expCentreTotalsCell}>
                                    Total
                                </StyledTableCell>
                                {stageConfig.map((stage) => {
                                    const lte = totals?.[stage.key]?.lte ?? 0;
                                    const gt = totals?.[stage.key]?.gt ?? 0;
                                    const lteAlert = isOtherDesignStagesAlert(stage.key, lte);
                                    const gtAlert = isOtherDesignStagesAlert(stage.key, gt);
                                    return (
                                        <React.Fragment key={`total-${stage.key}`}>
                                            <StyledTableCell
                                                className={`${classes.bordercolors} ${classes.totalsRowCell} ${
                                                    lteAlert ? classes.otherDesignStagesAlertCell : ''
                                                }`}
                                            >
                                                {renderCount(
                                                    lte,
                                                    () =>
                                                        openDetails({
                                                            experienceCentre: 'Total',
                                                            stageKey: stage.key,
                                                            bucket: 'lte',
                                                            label: `All Centres · ${stage.label} · <= ${stage.threshold} Days`,
                                                            count: lte,
                                                        }),
                                                    { isTotalsRow: true, isOtherDesignAlert: lteAlert }
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell
                                                className={`${classes.bordercolors} ${classes.totalsRowCell} ${
                                                    gtAlert ? classes.otherDesignStagesAlertCell : ''
                                                }`}
                                            >
                                                {renderCount(
                                                    gt,
                                                    () =>
                                                        openDetails({
                                                            experienceCentre: 'Total',
                                                            stageKey: stage.key,
                                                            bucket: 'gt',
                                                            label: `All Centres · ${stage.label} · > ${stage.threshold} Days`,
                                                            count: gt,
                                                        }),
                                                    { isTotalsRow: true, isOtherDesignAlert: gtAlert }
                                                )}
                                            </StyledTableCell>
                                        </React.Fragment>
                                    );
                                })}
                                <StyledTableCell className={`${classes.bordercolors} ${classes.totalsRowCell}`}>
                                    {renderCount(
                                        grandTotal || getRowGrandTotal(totals),
                                        () =>
                                            openDetails({
                                                experienceCentre: 'Total',
                                                stageKey: '',
                                                bucket: 'all',
                                                label: 'All Centres · Grand Total',
                                                count: grandTotal || getRowGrandTotal(totals),
                                            }),
                                        { isGrandTotal: true, isTotalsRow: true }
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                        {!rows.length && (
                            <TableRow>
                                <StyledTableCell colSpan={2 + stageConfig.length * 2 + 1} className={classes.emptyState}>
                                    No Design stage leads found for the selected filters.
                                </StyledTableCell>
                            </TableRow>
                        )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            <Modal
                title={detailTitle}
                centered
                visible={detailOpen}
                onCancel={() => setDetailOpen(false)}
                width="92%"
                style={{ maxWidth: 1280, top: 24 }}
                maskClosable
                footer={null}
                destroyOnClose
                bodyStyle={{
                    padding: '12px 24px 20px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                }}
            >
                <div className={classes.detailModalContent}>
                <div className={classes.csv}>
                    <CSVLink
                        filename="design-stage-days-details.csv"
                        data={csvData}
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            style={{ backgroundColor: '#355025', color: '#FFF2EB' }}
                            variant="contained"
                        >
                            Download CSV
                        </Button>
                    </CSVLink>
                </div>

                <TableContainer
                    ref={detailTableRef}
                    component={Paper}
                    className={classes.detailTable}
                    elevation={1}
                >
                    <Table size="small" stickyHeader className={classes.detailTableInner}>
                        <TableHead>
                            <TableRow>
                                <StyledTableCell>Sl. No.</StyledTableCell>
                                <StyledTableCell>Project No.</StyledTableCell>
                                <StyledTableCell>Lead No.</StyledTableCell>
                                <StyledTableCell>Client Name</StyledTableCell>
                                <StyledTableCell>Current Stage</StyledTableCell>
                                <StyledTableCell>{detailStageDateColumnLabel}</StyledTableCell>
                                <StyledTableCell>Value (INR)</StyledTableCell>
                                <StyledTableCell>Sales Won Value (INR)</StyledTableCell>
                                <StyledTableCell>Design Won Value (INR)</StyledTableCell>
                                <StyledTableCell>City</StyledTableCell>
                                <StyledTableCell>Exp. Ctr. Name</StyledTableCell>
                                <StyledTableCell>Design Experience Center</StyledTableCell>
                                <StyledTableCell>Sales Won Date</StyledTableCell>
                                <StyledTableCell>Design Sign-Off Date</StyledTableCell>
                                <StyledTableCell>Material Delivery Date</StyledTableCell>
                                <StyledTableCell>Actual Completion Date</StyledTableCell>
                                <StyledTableCell>Latest Notes/Date</StyledTableCell>
                                <StyledTableCell>Latest Deal Activity</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {detailRows.map((data, index) => {
                                const detailStripe =
                                    index % 2 === 0 ? classes.detailBodyCellOdd : classes.detailBodyCellEven;
                                const detailCell = `${classes.detailBodyCell} ${detailStripe}`;
                                return (
                                <StyledTableRow key={data._id || index}>
                                    <StyledTableCell className={detailCell}>{index + 1}</StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {data?.leadId?.erpProjectNo ? (
                                            <Link
                                                to={`/project-logs/${data?.leadId?._id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#1d4ed8', textDecoration: 'underline' }}
                                            >
                                                {data.leadId.erpProjectNo}
                                            </Link>
                                        ) : (
                                            'NA'
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {data?.leadId?.lead_no ? (
                                            <Link
                                                to={`/project-logs/${data?.leadId?._id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#1d4ed8', textDecoration: 'underline' }}
                                            >
                                                {data.leadId.lead_no}
                                            </Link>
                                        ) : (
                                            'NA'
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>{data?.leadId?.customerId?.name || '—'}</StyledTableCell>
                                    <StyledTableCell className={detailCell}>{data?.leadId?.currentStage || '—'}</StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {formatDate(resolveDetailStageDate(data, detailStageKey, stageConfig))}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {data?.leadId?.staticSalesWonBudget != null
                                            ? Number(data.leadId.staticSalesWonBudget).toFixed(2)
                                            : 'NA'}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {data?.leadId?.staticSalesWonBudget != null
                                            ? Number(data.leadId.staticSalesWonBudget).toFixed(2)
                                            : 'NA'}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {data?.leadId?.staticDesignWonBudget != null
                                            ? Number(data.leadId.staticDesignWonBudget).toFixed(2)
                                            : 'NA'}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {data?.salesExpCenter?.locationId?.name
                                            || data?.leadId?.experienceCenterId?.locationId?.name
                                            || '—'}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>{data?.salesExpCenter?.name || '—'}</StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {data?.DesignExpCenter?.name
                                            || data?.leadId?.designWonExpCtrId?.name
                                            || '—'}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {formatDate(data?.SalesWonDate || data?.leadId?.leadWonDate)}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {formatDate(data?.leadId?.customerDesignSignOffDate)}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {formatDate(data?.leadId?.materialDispatchedDate)}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        {formatDate(data?.leadId?.executionCompletionDate)}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        <Tooltip title={data?.latestNotesMsg || 'No notes'}>
                                            <IconButton size="small">
                                                <InfoIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {formatDate(data?.latestNotesDate)}
                                    </StyledTableCell>
                                    <StyledTableCell className={detailCell}>
                                        <Tooltip title={data?.latestDealMsg || 'No activity'}>
                                            <IconButton size="small">
                                                <InfoIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {formatDate(data?.latestDealDate)}
                                    </StyledTableCell>
                                </StyledTableRow>
                                );
                            })}
                            {!detailRows.length && (
                                <TableRow>
                                    <StyledTableCell colSpan={18}>No projects found for this count.</StyledTableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                </div>
            </Modal>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    setLoading: (data) => dispatch(setLoading(data)),
    setSnackInfo: (data, type) => dispatch(setSnackInfo(data, type)),
});

export default connect(null, mapDispatchToProps)(DesignStageDaysSummary);
